import PlaybackRangeBar from '@/components/PlaybackRangeBar.vue'
import {
  isFullPlaybackClip,
  slicePointsByRatioRange,
  isParkingInPlaybackClip,
  computeTripSummaryFromPoints,
  normalizeParkingEventTimeMs,
  computePlaybackRangeElapsedBounds,
  formatDurationZh,
  ratioToElapsedSeconds,
  PLAYBACK_RANGE_GEOCODE_DEBOUNCE_MS
} from '@/utils/playbackRangeClip'

const PLAYBACK_RANGE_SETTLE_MS = PLAYBACK_RANGE_GEOCODE_DEBOUNCE_MS

/**
 * 关联播放范围条：主进度条在选区内播放，下方缩略条可拖动选区。
 * 依赖宿主组件：trackPlaybackTotalSeconds、processedTrackData、speedChartPoints、pathNavigatorActive 等。
 */
export default {
  components: {
    PlaybackRangeBar
  },
  data() {
    return {
      /** 已生效的选区（数据/播放/地图裁剪用） */
      playbackRangeStart: 0,
      playbackRangeEnd: 100,
      /** 关联条拖动中的临时选区（仅缩略条手柄跟随） */
      playbackRangeDraftStart: 0,
      playbackRangeDraftEnd: 100,
      /** 当前选区已播完，下次点播放应从选区起点重播 */
      playbackCompletedInRange: false,
      /** 关联条拖动防抖中：右侧数据与地图裁剪保持上一次生效选区不变 */
      playbackRangeDragging: false,
      /** 关联条提交后短暂锁定：主进度条保持在选区起点 00:00:00，避免导航器回写跳位 */
      playbackRangeProgressLocked: false,
      /** 选区复位/刚点播放后短时忽略终点判定，避免 PathNavigator 复位瞬时高比例误停 */
      _playbackEndGuardUntil: 0
    }
  },
  computed: {
    /** PlaybackRangeBar 绑定：拖动时跟手，数据面板仍用已生效选区 */
    playbackRangeBarStart() {
      return this.playbackRangeDraftStart
    },
    playbackRangeBarEnd() {
      return this.playbackRangeDraftEnd
    },
    isPlaybackRangeClipped() {
      return !isFullPlaybackClip(this.playbackRangeStartRatio(), this.playbackRangeEndRatio())
    },
    /** 关联条选区内的轨迹点（表格/导出/速度曲线/行程摘要用；播放仍用全量 processedTrackData） */
    rangeFilteredTrackData() {
      const all = this.processedTrackData
      if (!all || !all.length) return []
      if (!this.isPlaybackRangeClipped) return all
      return slicePointsByRatioRange(
        all,
        this.playbackRangeStartRatio(),
        this.playbackRangeEndRatio()
      ).sliced
    },
    /** 关联条选区内的停靠点 */
    rangeFilteredParkingData() {
      const all = this.processedParkingData
      if (!all || !all.length) return []
      if (!this.isPlaybackRangeClipped) return all
      const sr = this.playbackRangeStartRatio()
      const er = this.playbackRangeEndRatio()
      const clipTrack = this.rangeFilteredTrackData
      const traceList = Array.isArray(this.runRoute) ? this.runRoute : []
      const startMs = clipTrack.length ? clipTrack[0].timeMs : null
      const endMs = clipTrack.length ? clipTrack[clipTrack.length - 1].timeMs : null
      const useTimeClip = startMs != null && endMs != null
      const lo = useTimeClip ? Math.min(startMs, endMs) : null
      const hi = useTimeClip ? Math.max(startMs, endMs) : null
      return all.filter((parking) => {
        const routeIndex = parking.routeIndex
        if (Number.isFinite(Number(routeIndex)) && Number(routeIndex) >= 0) {
          return isParkingInPlaybackClip(parking, traceList, sr, er, routeIndex)
        }
        if (useTimeClip) {
          const parkingMs = normalizeParkingEventTimeMs(parking)
          if (parkingMs == null) return false
          return parkingMs >= lo && parkingMs <= hi
        }
        return isParkingInPlaybackClip(parking, traceList, sr, er, routeIndex)
      })
    },
    /** 行程卡片展示的总里程/总耗时：全轨迹用 props，选区用区间重算 */
    displayTripSummary() {
      const fallback =
        this.tripSummary && typeof this.tripSummary === 'object'
          ? this.tripSummary
          : { distance: '0km', duration: '0时0分0秒' }
      if (!this.isPlaybackRangeClipped) return fallback
      const summary = computeTripSummaryFromPoints(this.rangeFilteredTrackData, fallback)
      const windowSec = this.playbackWindowSeconds
      if (windowSec > 1e-3 && !this.rangeFilteredTrackData.some(point => point.segmentKey !== undefined)) {
        summary.duration = formatDurationZh(windowSec)
      }
      return summary
    },
    /** 选区在整条轨迹时间轴上的起止/窗口（秒），与卡片总耗时同源 */
    playbackRangeElapsedBounds() {
      return computePlaybackRangeElapsedBounds(
        this.processedTrackData,
        this.playbackRangeStartRatio(),
        this.playbackRangeEndRatio()
      )
    },
    playbackRangeStartSeconds() {
      return this.playbackRangeElapsedBounds.startSec
    },
    playbackRangeEndSeconds() {
      return this.playbackRangeElapsedBounds.endSec
    },
    playbackWindowSeconds() {
      return this.playbackRangeElapsedBounds.windowSec
    },
    playbackProgressRatioFull() {
      const total = this.trackPlaybackTotalSeconds
      if (total <= 0) return 0
      return Math.min(1, Math.max(0, this.playElapsedSeconds / total))
    }
  },
  methods: {
    syncTotalTime() {
      const secs = Math.max(0, Math.floor(this.playbackWindowSeconds))
      this.totalTime = this.formatTime(secs)
    },
    elapsedToWindowProgress(elapsed) {
      const windowStart = this.playbackRangeStartSeconds
      const windowTotal = this.playbackWindowSeconds
      if (windowTotal <= 0) return 0
      return Math.min(100, Math.max(0, ((elapsed - windowStart) / windowTotal) * 100))
    },
    windowProgressToElapsed(pct) {
      const windowStart = this.playbackRangeStartSeconds
      const windowTotal = this.playbackWindowSeconds
      return windowStart + (windowTotal > 0 ? (Number(pct) / 100) * windowTotal : 0)
    },
    windowRelativeElapsed(elapsed) {
      return Math.max(0, Math.min(this.playbackWindowSeconds, elapsed - this.playbackRangeStartSeconds))
    },
    clampPlaybackToRange() {
      const start = this.playbackRangeStartSeconds
      const end = this.playbackRangeEndSeconds
      if (this.playElapsedSeconds < start) {
        this.playElapsedSeconds = start
      } else if (this.playElapsedSeconds > end) {
        this.playElapsedSeconds = end
      }
    },
    onPlaybackRangeChange({ start, end }) {
      const nextStart = Math.min(100, Math.max(0, Number(start) || 0))
      const nextEnd = Math.max(nextStart, Math.min(100, Number(end) || 100))
      this.playbackRangeDraftStart = nextStart
      this.playbackRangeDraftEnd = nextEnd
      this.playbackRangeDragging = true
      this.schedulePlaybackRangeSettled()
    },
    schedulePlaybackRangeSettled() {
      if (this._playbackRangeSettleTimer) {
        clearTimeout(this._playbackRangeSettleTimer)
      }
      this._playbackRangeSettleTimer = setTimeout(() => {
        this._playbackRangeSettleTimer = null
        this.onPlaybackRangeSettled()
      }, PLAYBACK_RANGE_SETTLE_MS)
    },
    /** 防抖结束：一次性提交选区并刷新全部关联数据 */
    onPlaybackRangeSettled() {
      this.playbackRangeDragging = false
      this.commitPlaybackRangeChange()
      if (typeof this.resolveTripStartEndLocation === 'function') {
        this.resolveTripStartEndLocation()
      }
      if (Number.isFinite(Number(this.parkingPageNum))) {
        this.parkingPageNum = 1
      }
      if (Number.isFinite(Number(this.trackPageNum))) {
        this.trackPageNum = 1
      }
    },
    commitPlaybackRangeChange() {
      if (typeof this.clearTripEndpointResolveTimers === 'function') {
        this.clearTripEndpointResolveTimers()
      }
      this.playbackRangeStart = this.playbackRangeDraftStart
      this.playbackRangeEnd = this.playbackRangeDraftEnd
      if (this.isPlaying) {
        this.pauseTrack()
      }
      this.playElapsedSeconds = this.playbackRangeStartSeconds
      this.playbackCompletedInRange = false
      this.playbackRangeProgressLocked = true
      this._playbackEndGuardUntil = Date.now() + 600
      this.suppressSliderSeek = true
      this.videoProgress = 0
      this.currentTime = '00:00:00'
      this.$nextTick(() => {
        this.suppressSliderSeek = false
        this.$nextTick(() => {
          this.playbackRangeProgressLocked = false
        })
      })
      this.syncTotalTime()
      const rangeRatio = this.elapsedToFullNavigatorRatio(this.playbackRangeStartSeconds)
      this.emitPlaybackRangeChange()
      if (this.pathNavigatorActive) {
        // rangeReset：地图侧 applyPlaybackRangeClip 已复位车标，勿再 seek 回写进度（距离≠时间会导致进度条跳位）
        this.$emit('path-nav-control', { type: 'seek', ratio: rangeRatio, rangeReset: true })
      }
      this.applyPlaybackPosition()
    },
    clearPlaybackRangeSettleTimer() {
      if (this._playbackRangeSettleTimer) {
        clearTimeout(this._playbackRangeSettleTimer)
        this._playbackRangeSettleTimer = null
      }
      this.playbackRangeDragging = false
      this.playbackRangeDraftStart = this.playbackRangeStart
      this.playbackRangeDraftEnd = this.playbackRangeEnd
    },
    emitPlaybackRangeChange() {
      this.$emit('playback-range-change', {
        startRatio: this.playbackRangeStartRatio(),
        endRatio: this.playbackRangeEndRatio()
      })
    },
    playbackRangeStartRatio() {
      return Math.min(1, Math.max(0, (Number(this.playbackRangeStart) || 0) / 100))
    },
    playbackRangeEndRatio() {
      const startPct = Math.min(100, Math.max(0, Number(this.playbackRangeStart) || 0))
      const endPct = Math.max(startPct, Math.min(100, Number(this.playbackRangeEnd) || 100))
      return endPct / 100
    },
    emitPathNavSeekRatio(r) {
      const ratio = Math.min(1, Math.max(0, Number(r)))
      this.$emit('path-nav-control', { type: 'seek', ratio })
    },
    /** 拖动中仅更新了时间与折线，未 commit 到 PathNavigator 时，播放/暂停前先按当前进度对齐车标 */
    emitPathNavSeekFromCurrentElapsed() {
      this.$emit('path-nav-control', {
        type: 'seek',
        ratio: this.elapsedToFullNavigatorRatio(this.playElapsedSeconds)
      })
    },
    /** @deprecated 保留旧名；进度条 seek 后不再自动续播，需用户点播放 */
    emitPathNavSeekRatioWithResumeIfPlaying(r) {
      this.emitPathNavSeekRatio(r)
    },
    onProgressChange(val) {
      const trackData = this.processedTrackData
      if (!trackData.length) return
      // 保持 sliderDragging 至 seek 结束，避免 PathNavigator move 回写用距离比例覆盖时间轴进度
      this.sliderDragging = true
      if (this.isPlaying) {
        if (this.pathNavigatorActive) {
          this.$emit('path-nav-control', { type: 'pause' })
        }
        this.stopPlaybackTimer()
      }
      if (this.pathNavigatorActive) {
        this.seekToProgress(val, { commitNavSeek: true })
      } else {
        this.seekToProgress(val)
      }
      this.sliderDragging = false
    },
    resetPlaybackRangeState() {
      this.clearPlaybackRangeSettleTimer()
      this.playbackRangeStart = 0
      this.playbackRangeEnd = 100
      this.playbackRangeDraftStart = 0
      this.playbackRangeDraftEnd = 100
      this.emitPlaybackRangeChange()
    },
    preparePlaybackRangeBeforePlay() {
      const total = this.trackPlaybackTotalSeconds
      const rangeStart = this.playbackRangeStartSeconds
      const rangeEnd = this.playbackRangeEndSeconds
      this.clampPlaybackToRange()
      const endRatio = this.playbackRangeEndRatio()
      const currentRatio = total > 0 ? Math.min(1, this.playElapsedSeconds / total) : 0
      const atRangeEnd =
        this.playbackCompletedInRange ||
        currentRatio >= endRatio - 1e-3 ||
        (rangeEnd > 0 && this.playElapsedSeconds >= rangeEnd - 0.05)
      if (atRangeEnd) {
        this.playElapsedSeconds = rangeStart
        this.playbackCompletedInRange = false
      }
      this.suppressSliderSeek = true
      this.videoProgress = this.elapsedToWindowProgress(this.playElapsedSeconds)
      this.currentTime = this.formatTime(Math.floor(this.windowRelativeElapsed(this.playElapsedSeconds)))
      this.$nextTick(() => {
        this.suppressSliderSeek = false
      })
      this.syncTotalTime()
    },
    syncNavigatorStopped() {
      this.isPlaying = false
      this.stopPlaybackTimer()
      this.playbackCompletedInRange = true
    },
    startPathNavPlaybackFromCurrent() {
      if (this.sliderDragging) {
        this.emitPathNavSeekFromCurrentElapsed()
      }
      this.sliderDragging = false
      this.clampPlaybackToRange()
      const totalNav = this.trackPlaybackTotalSeconds
      const atWindowStart =
        Number(this.videoProgress) <= 0.01 ||
        this.elapsedToWindowProgress(this.playElapsedSeconds) <= 0.01
      const payload = {
        type: 'play',
        speedMult: this.currentPathNavSpeedMult()
      }
      if (atWindowStart) {
        payload.fromWindowStart = true
      } else if (totalNav > 0) {
        payload.ratio = this.elapsedToFullNavigatorRatio(this.playElapsedSeconds)
      }
      this.isPlaying = true
      this.playbackCompletedInRange = false
      this._playbackEndGuardUntil = Date.now() + 600
      this.$emit('path-nav-control', payload)
      const pos = this.interpolatePositionAtElapsed(this.playElapsedSeconds)
      if (pos) {
        this.$emit('center-map', [pos.lon, pos.lat])
      }
    },
    formatSliderTooltip(val) {
      const windowTotal = this.playbackWindowSeconds
      if (!windowTotal || !this.processedTrackData.length) return '00:00:00'
      const pct = Number(val)
      const secs = Math.floor(Math.min(Math.max(0, (pct / 100) * windowTotal), windowTotal))
      return this.formatTime(secs)
    },
    resetTrajectoryPlayback() {
      this.clearPlaybackRangeSettleTimer()
      this.stopPlaybackTimer()
      this.sliderDragging = false
      this.playElapsedSeconds = 0
      this.playbackCompletedInRange = false
      this.resetPlaybackRangeState()
      this.suppressSliderSeek = true
      this.videoProgress = 0
      this.$nextTick(() => {
        this.suppressSliderSeek = false
      })
      this.currentTime = '00:00:00'
      this.syncTotalTime()
      this.destroyVehicleMarker()
      this.$nextTick(() => {
        if (this.processedTrackData && this.processedTrackData.length) {
          this.applyPlaybackPosition()
        }
      })
    },
    seekToProgress(percent, opts = {}) {
      this.clearTrackPointPreview()
      const trackData = this.processedTrackData
      if (!trackData.length) return
      const total = this.trackPlaybackTotalSeconds
      const pct = Number(percent)
      const commitNavSeek = opts.commitNavSeek !== false
      const elapsed = this.windowProgressToElapsed(pct)
      this.playbackCompletedInRange = false
      if (this.pathNavigatorActive) {
        this.suppressSliderSeek = true
        this.videoProgress = pct
        this.$nextTick(() => {
          this.suppressSliderSeek = false
        })
        this.playElapsedSeconds = elapsed
        this.currentTime = this.formatTime(Math.floor(this.windowRelativeElapsed(this.playElapsedSeconds)))
        const pos = this.interpolatePositionAtElapsed(this.playElapsedSeconds)
        const meta = this.metaAtPlaybackElapsed(this.playElapsedSeconds)
        const r = this.elapsedToFullNavigatorRatio(this.playElapsedSeconds)
        this.$emit('playback-progress', {
          ratio: r,
          playElapsedSeconds: this.playElapsedSeconds,
          totalSeconds: total,
          isPlaying: this.isPlaying,
          lon: pos ? pos.lon : null,
          lat: pos ? pos.lat : null,
          ...meta
        })
        if (commitNavSeek) {
          this.emitPathNavSeekRatio(r)
        } else {
          // 拖动中：预览同步车标，不回写右侧进度（避免距离/时间比例互相覆盖）
          this.$emit('path-nav-control', { type: 'seek', ratio: r, preview: true })
        }
        return
      }
      this.playElapsedSeconds = elapsed
      this.suppressSliderSeek = true
      this.videoProgress = pct
      this.$nextTick(() => {
        this.suppressSliderSeek = false
      })
      this.applyPlaybackPosition()
    },
    /** 导航器全轨迹比例 → 时间轴秒数（相邻点插值，避免距离比例取整后时间一跳几十秒） */
    navigatorRatioToElapsedSeconds(fullRatio) {
      return ratioToElapsedSeconds(
        this.processedTrackData,
        fullRatio,
        this.trackPlaybackTotalSeconds
      )
    },
    /** 时间轴秒数 → 导航器全轨迹比例（与 navigatorRatioToElapsedSeconds 互逆，供 seek/播放对齐） */
    elapsedToFullNavigatorRatio(elapsedSec) {
      const pts = this.processedTrackData
      if (!pts || pts.length < 2) return 0
      const n = pts.length
      const totalSec = this.trackPlaybackTotalSeconds
      const clamped = Math.max(0, Math.min(Number(elapsedSec) || 0, totalSec))
      const t0 = pts[0].timeMs
      const tEnd = pts[n - 1].timeMs
      if (t0 == null || tEnd == null) {
        return totalSec > 0 ? clamped / totalSec : 0
      }
      const targetMs = t0 + clamped * 1000
      if (targetMs <= t0) return 0
      if (targetMs >= tEnd) return 1
      for (let i = 0; i < n - 1; i++) {
        const a = pts[i].timeMs
        const b = pts[i + 1].timeMs
        if (a == null || b == null) continue
        if (targetMs >= a && targetMs <= b) {
          const denom = b - a
          const t = denom > 0 ? (targetMs - a) / denom : 0
          return Math.min(1, Math.max(0, (i + t) / (n - 1)))
        }
      }
      return 1
    },
    syncFromPathNavigatorRatio(ratio, navPos) {
      const pts = this.processedTrackData
      if (!pts || !pts.length) return
      // 进度条拖动/点击中：时间与 videoProgress 由 seekToProgress 接管，
      // 勿用导航器距离比例回写（否则只剩 tooltip/时间跳动、滑块条不动或跳到错误位置）
      if (this.sliderDragging) return
      const total = this.trackPlaybackTotalSeconds
      const r = Math.min(1, Math.max(0, ratio))
      const rangeStart = this.playbackRangeStartSeconds
      const rangeEnd = this.playbackRangeEndSeconds
      if (!this.playbackRangeProgressLocked) {
        this.playElapsedSeconds = this.navigatorRatioToElapsedSeconds(r)
        if (this.playElapsedSeconds < rangeStart) {
          this.playElapsedSeconds = rangeStart
        }
        if (this.playElapsedSeconds > rangeEnd) {
          this.playElapsedSeconds = rangeEnd
        }
      } else {
        this.playElapsedSeconds = rangeStart
      }
      this.suppressSliderSeek = true
      if (this.playbackRangeProgressLocked) {
        this.videoProgress = 0
        this.currentTime = '00:00:00'
      } else {
        this.videoProgress = this.elapsedToWindowProgress(this.playElapsedSeconds)
        this.currentTime = this.formatTime(Math.floor(this.windowRelativeElapsed(this.playElapsedSeconds)))
      }
      this.$nextTick(() => {
        this.suppressSliderSeek = false
      })
      const meta = this.metaAtPlaybackElapsed(this.playElapsedSeconds)
      let lon = null
      let lat = null
      if (navPos && navPos.lon != null && navPos.lat != null) {
        lon = Number(navPos.lon)
        lat = Number(navPos.lat)
      } else {
        const pos = this.interpolatePositionAtElapsed(this.playElapsedSeconds)
        lon = pos ? pos.lon : null
        lat = pos ? pos.lat : null
      }
      this.$emit('playback-progress', {
        ratio: r,
        playElapsedSeconds: this.playElapsedSeconds,
        totalSeconds: total,
        isPlaying: this.isPlaying,
        lon,
        lat,
        ...meta
      })
      if (
        this.isPlaying &&
        Date.now() >= (this._playbackEndGuardUntil || 0) &&
        this.playElapsedSeconds >= rangeEnd - 1e-3 &&
        this.elapsedToWindowProgress(this.playElapsedSeconds) >= 99.5
      ) {
        this.playbackCompletedInRange = true
        this.pauseTrack()
      }
    },
    schedulePlayInterval() {
      const trackData = this.processedTrackData
      if (!trackData.length) return
      if (this.playInterval) {
        clearInterval(this.playInterval)
        this.playInterval = null
      }
      if (!this.isPlaying) return
      const speed = parseInt(this.playbackSpeed, 10) || 1
      const frameMs = 16
      this.playInterval = setInterval(() => {
        if (this.sliderDragging) return
        const points = this.processedTrackData
        if (!points.length) {
          this.pauseTrack()
          return
        }
        const total = this.trackPlaybackTotalSeconds
        if (total <= 0) {
          this.pauseTrack()
          return
        }
        this.playElapsedSeconds += speed * (frameMs / 1000)
        const end = this.playbackRangeEndSeconds
        if (this.playElapsedSeconds >= end) {
          this.playElapsedSeconds = end
          this.applyPlaybackPosition()
          this.pauseTrack()
          return
        }
        this.applyPlaybackPosition()
      }, frameMs)
    },
    applyPlaybackPosition(opts = {}) {
      const centerMap = opts.centerMap === true
      const pts = this.processedTrackData
      if (!pts || !pts.length) return
      if (this.pathNavigatorActive) {
        const pos = this.interpolatePositionAtElapsed(this.playElapsedSeconds)
        if (!pos) return
        const total = this.trackPlaybackTotalSeconds
        const ratio = total > 0 ? Math.min(1, Math.max(0, this.playElapsedSeconds / total)) : 0
        const meta = this.metaAtPlaybackElapsed(this.playElapsedSeconds)
        this.$emit('playback-progress', {
          ratio,
          playElapsedSeconds: this.playElapsedSeconds,
          totalSeconds: total,
          isPlaying: this.isPlaying,
          lon: pos.lon,
          lat: pos.lat,
          ...meta
        })
        if (centerMap) {
          this.$emit('center-map', [pos.lon, pos.lat])
        }
        return
      }
      const pos = this.interpolatePositionAtElapsed(this.playElapsedSeconds)
      if (!pos) return
      if (!this.vehicleMarker) {
        this.createVehicleMarker()
      }
      if (this.vehicleMarker && !this.trackPointPreviewActive) {
        this.vehicleMarker.setPosition([pos.lon, pos.lat])
        if (pos.bearing != null) {
          this.vehicleMarker.setAngle(pos.bearing)
        }
        if (centerMap) {
          this.$emit('center-map', [pos.lon, pos.lat])
        }
      }
      const total = this.trackPlaybackTotalSeconds
      const pct = this.elapsedToWindowProgress(this.playElapsedSeconds)
      if (!this.sliderDragging) {
        this.suppressSliderSeek = true
        this.videoProgress = pct
        this.$nextTick(() => {
          this.suppressSliderSeek = false
        })
      }
      this.currentTime = this.formatTime(Math.floor(this.windowRelativeElapsed(this.playElapsedSeconds)))
      const ratio = total > 0 ? Math.min(1, Math.max(0, this.playElapsedSeconds / total)) : 0
      const meta = this.metaAtPlaybackElapsed(this.playElapsedSeconds)
      this.$emit('playback-progress', {
        ratio,
        playElapsedSeconds: this.playElapsedSeconds,
        totalSeconds: total,
        isPlaying: this.isPlaying,
        lon: pos.lon,
        lat: pos.lat,
        ...meta
      })
    },
    playTrack() {
      if (this.isPlaying) return
      this.clearTrackPointPreview()
      const trackData = this.processedTrackData
      if (!trackData || trackData.length === 0) {
        this.$message.warning('没有轨迹数据可播放')
        return
      }
      if (this.qualityMode && (trackData.length < 2 || this.trackPlaybackTotalSeconds <= 0)) {
        this.$message.warning('单点或零时长轨迹仅支持定位与明细')
        return
      }
      this.preparePlaybackRangeBeforePlay()
      if (this.pathNavigatorActive) {
        this.startPathNavPlaybackFromCurrent()
        return
      }
      if (!this.vehicleMarker) {
        this.createVehicleMarker()
      }
      this.sliderDragging = false
      this.isPlaying = true
      this.applyPlaybackPosition({ centerMap: true })
      this.schedulePlayInterval()
    },
    resumeTrack() {
      if (this.isPlaying) return
      this.clearTrackPointPreview()
      const trackData = this.processedTrackData
      if (!trackData || trackData.length === 0) {
        this.$message.warning('没有轨迹数据可播放')
        return
      }
      if (this.qualityMode && (trackData.length < 2 || this.trackPlaybackTotalSeconds <= 0)) {
        this.$message.warning('单点或零时长轨迹仅支持定位与明细')
        return
      }
      this.preparePlaybackRangeBeforePlay()
      if (this.pathNavigatorActive) {
        this.startPathNavPlaybackFromCurrent()
        return
      }
      if (!this.vehicleMarker) {
        this.createVehicleMarker()
      }
      this.sliderDragging = false
      this.isPlaying = true
      this.applyPlaybackPosition({ centerMap: true })
      this.schedulePlayInterval()
    }
  },
  beforeDestroy() {
    this.clearPlaybackRangeSettleTimer()
  }
}
